import { Card, CardValue, Suit, WrapResult } from '../types/poker';

export class Deck {
  private cards: Card[];

  constructor() {
    this.cards = [];
    // Fill the deck with all possible cards
    Object.values(Suit).forEach(suit => {
      for (let value = CardValue.TWO; value <= CardValue.ACE; value++) {
        this.cards.push({ suit, value: value as CardValue });
      }
    });
    this.shuffle();
  }

  public shuffle(): void {
    for (let i = this.cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];
    }
  }

  public getAndRemoveRank(value: CardValue): Card {
    const index = this.cards.findIndex(card => card.value === value);
    if (index === -1) {
      throw new Error('Could not find card rank in getAndRemoveRank');
    }
    const [card] = this.cards.splice(index, 1);
    return card;
  }

  public dealCard(): Card {
    if (this.cards.length === 0) {
      throw new Error('No cards left in deck');
    }
    return this.cards.pop()!;
  }

  public putCardBack(card: Card): void {
    this.cards.unshift(card);
  }
}

/** One concrete hand shape that produces a specific answer. */
interface HandConfig {
  holeCount: number;
  template: string;
  startValue: number;
}

/** Catalog of every achievable answer and the configs that produce it. */
interface AnswerCatalog {
  /** "outs-nutOuts" answer key -> hand configs that produce it. */
  byAnswer: Map<string, HandConfig[]>;
  /** the distinct answer keys, aligned with `cumulative`. */
  keys: string[];
  /** cumulative sampling weights aligned with `keys` (last entry = 1). */
  cumulative: number[];
}

export class WrapGenerator {
  private runTemplate: string;
  private board: Card[];
  private holeCards: Card[];
  private outs: number;
  private nutOuts: number;

  private static catalog: AnswerCatalog | null = null;

  /**
   * @param defer when true, skips hand generation. Used internally to build
   *   the answer catalog without triggering a full generate().
   */
  constructor(defer: boolean = false) {
    this.runTemplate = '';
    this.board = [];
    this.holeCards = [];
    this.outs = 0;
    this.nutOuts = 0;
    if (!defer) {
      this.generate();
    }
  }

  private firstFourHasThree(): boolean {
    let count = 0;
    for (let i = 0; i < 4; i++) {
      if (this.runTemplate[i] === 'H') {
        count++;
      }
    }
    return count >= 3;
  }

  private lastFourHasThree(): boolean {
    let count = 0;
    for (let i = this.runTemplate.length - 1; i > this.runTemplate.length - 1 - 4; i--) {
      if (this.runTemplate[i] === 'H') {
        count++;
      }
    }
    return count >= 3;
  }

  private topAndBottom2H2B(): boolean {
    let hcount = 0;
    let bcount = 0;
    
    // Check first four cards
    for (let i = 0; i < 4; i++) {
      if (this.runTemplate[i] === 'H') {
        hcount++;
      } else {
        bcount++;
      }
    }
    if (!(hcount === 2 && bcount === 2)) {
      return false;
    }
    
    // Check last four cards
    hcount = 0;
    bcount = 0;
    for (let i = this.runTemplate.length - 1; i > this.runTemplate.length - 1 - 4; i--) {
      if (this.runTemplate[i] === 'H') {
        hcount++;
      } else {
        bcount++;
      }
    }
    return hcount === 2 && bcount === 2;
  }

  private isStraight(): boolean {
    const straightTemplates = [
      "BBHBHHH", "HBBHBHH", "HHBBHBH", "HHHBBHB",
      "BHBBHHH", "HBHBBHH", "HHBHBBH", "HHHBHBB",
      "BBBHHHH", "HBBBHHH", "HHBBBHH", "HHHBBBH", "HHHHBBB",
      "BBHHBHH", "HBBHHBH", "HHBBHHB",
      "HHBHHBB", "HBHHBBH", "BHHBBHH",
      "BHBHBHH", "HBHBHBH", "HHBHBHB"
    ];
    return straightTemplates.includes(this.runTemplate);
  }

  private calcOuts(): void {
    // 4 hole cards, board 3 case
    if (this.holeCards.length === 4) {
      if (this.board.length === 3) {
        if (this.runTemplate === "BBHHHHB" || this.runTemplate === "BHHHHBB" ||
            this.runTemplate === "BBHHHBH" || this.runTemplate === "HBHHHBB") {
          // BBHHHHB (13,13) BHHHHBB (13,3)  
          //                 BHHHHBBA (13,7)
          // ABHHHHB ( 9, 9) AHHHHBB (13,3)
          // BBHHHHA (13,13) BHHHHBA (9, 9)

          // BBHHHBH (13,13) HBHHHBB (13,3)
          //                 HBHHHBBA (13,7)
          // ABHHHBH ( 9, 9) ABHHHBB (13,3)
          // BBHHHBA (13,13) HBHHHBA ( 9,9)

          if (this.firstFourHasThree()) {
            if (this.board[this.board.length - 1].value === CardValue.KING) {
              this.outs = 13;
              this.nutOuts = 7;
            } else if (this.board[this.board.length - 1].value === CardValue.ACE) {
              this.outs = 9;
              this.nutOuts = 9;
            } else {
              this.outs = 13;
              this.nutOuts = 3;
            }
          } else {
            if (this.board[0].value === CardValue.ACE) {
              this.outs = 9;
              this.nutOuts = 9;
            } else {
              this.outs = 13;
              this.nutOuts = 13;
            }
          }
        } else if (this.runTemplate === "HBBHHHB" || this.runTemplate === "BHHHBBH") {
          // found this case with a bug
          // HBBHHHB (16, 16) BHHHBBH (16, 6)
          // ABBHHHB (12, 12) AHHHBBH (16, 6)
          // HBBHHHA (16, 16) BHHHBBA (12, 12)
          if (this.lastFourHasThree()) {
            this.outs = 16;
            this.nutOuts = 16;

            if (this.holeCards[0].value === CardValue.ACE) {
              this.outs -= 4;
              this.nutOuts -= 4;
            }
          } else if (this.firstFourHasThree()) {
            if (this.holeCards[this.holeCards.length - 1].value === CardValue.ACE) {
              this.outs = 12;
              this.nutOuts = 12;
            } else {
              this.outs = 16;
              this.nutOuts = 6;
            }
          }

        }else if (this.topAndBottom2H2B()) {
          // top and bottom consist of 2H and 2B cards
          // 20 out SD
          //BHHBHHB (20,14) or HBHBHHB (20,14) or BHHBHBH (20,14)
          //BHHBHHA (16,16) or HBHBHHA (16,16) or BHHBHBA (16,16)
          //AHHBHHB (16,10) or ABHBHHB (16,10) or AHHBHBH (16,10)

          this.outs = 20;
          this.nutOuts = 14;

          // Check for aces on either end
          const firstCard = this.runTemplate[0] === 'H' ? 
            this.holeCards[0] : this.board[0];
          
          if (firstCard.value === CardValue.ACE) {
            this.outs -= 4;
            this.nutOuts += 2;
          }

          const lastCard = this.runTemplate[this.runTemplate.length - 1] === 'H' ?
            this.holeCards[this.holeCards.length - 1] : 
            this.board[this.board.length - 1];

          if (lastCard.value === CardValue.ACE) {
            this.outs -= 4;
            this.nutOuts -= 4;
          }
        } else {
          // has to be case of either top or bottom four cards consist of 3H's and one B
          // 16 out SD
          // BHBHHBH (16,16) BHBHHHB (16,16) HBHHBHB (16, 6) BHHHBHB (16, 6)
          //                                 HBHHBHBA(16,10) BHHHBHBA(16,10)
          // AHBHHBH (12,12) AHBHHHB (12,12) ABHHBHB (16, 6) AHHHBHB (16, 6)
          //                                 
          // BHBHHBA (16,16) BHBHHHA (16,16) HBHHBHA (12,12) BHHHBHA (12,12)

          if (this.lastFourHasThree()) {
            this.outs = 16;
            this.nutOuts = 16;

            if (this.board[0].value === CardValue.ACE) {
              this.outs -= 4;
              this.nutOuts -= 4;
            }
          } else if (this.firstFourHasThree()) {
            if (this.board[this.board.length - 1].value === CardValue.ACE) {
              this.outs = 12;
              this.nutOuts = 12;
            } else if (this.board[this.board.length - 1].value === CardValue.KING) {
              this.outs = 16;
              this.nutOuts = 10;
            } else {
              this.outs = 16;
              this.nutOuts = 6;
            }
          }
        }
      } else if (this.board.length === 2) {
        // 4 hole cards, board 2 case
        if (this.runTemplate === "HHBBHH") {
          // HHBBHH (20,14)
          // AHBBHH (16,10)
          // HHBBHA (16,16)
          this.outs = 20;
          this.nutOuts = 14;

          if (this.holeCards[0].value === CardValue.ACE) {
            this.outs -= 4;
            this.nutOuts -= 4;
          }

          if (this.holeCards[this.holeCards.length - 1].value === CardValue.ACE) {
            this.outs -= 4;
            this.nutOuts = this.nutOuts - 4 + 6;
          }
        } else if (this.runTemplate === "HHHHBB" || this.runTemplate === "BBHHHH" ||
                   this.runTemplate === "HHHBHB" || this.runTemplate === "BHBHHH") {
          // 13 out SD
          //BBHHHH (13,13) HHHHBB (13, 3) 
          //                HHHHBBA(13, 7)
          //ABHHHH ( 9,9)  AHHHBB (13, 3)
          //BBHHHA (13,13) HHHHBA ( 9, 9)

          // 13 out SD
          // HHHBHB (13, 3) BHBHHH(13,13)
          // HHHBHBA(13, 7)
          // AHHBHB (13, 3) AHBHHH( 9,9 )
          // HHHBHA ( 9, 9) BHBHHA(13,13)

          if (this.firstFourHasThree()) {
            if (this.board[this.board.length - 1].value === CardValue.ACE) {
              this.outs = 9;
              this.nutOuts = 9;
            } else if (this.board[this.board.length - 1].value === CardValue.KING) {
              this.outs = 13;
              this.nutOuts = 7;
            } else {
              this.outs = 13;
              this.nutOuts = 3;
            }
          } else {
            if (this.board[0].value === CardValue.ACE) {
              this.outs = 9;
              this.nutOuts = 9;
            } else {
              this.outs = 13;
              this.nutOuts = 13;
            }
          }
        } else if (this.runTemplate === "HBHHHB" || this.runTemplate === "BHHHBH") {
          // 9 out SD
          // HBHHHB (9, 9) BHHHBH (9, 9)
          // ABHHHB (9, 9) AHHHBH (9, 9)
          // HBHHHA (9, 9) BHHHBA (9, 9)
          this.outs = 9;
          this.nutOuts = 9;
        } else if (this.runTemplate === "HBHHBH") {
          // 12 out SD
          // HBHHBH (12,12)
          // ABHHBH (12,12)
          // HBHHBA (12,12)
          this.outs = 12;
          this.nutOuts = 12;
        } else if (this.runTemplate === "BHHBHH" || this.runTemplate === "HHBHHB"){
          // 
          // BHHBHH (13, 13) HHBHHB(13, 7)
          // AHHBHH ( 9,  9) HHBHHA( 9, 9)
          if (this.runTemplate[0] === 'B'){
            if (this.board[0].value === CardValue.ACE) {
              this.outs = 9;
              this.nutOuts = 9;
            }else{
              this.outs = 13;
              this.nutOuts = 13;
            }
          }else{
            if (this.board[this.board.length - 1].value === CardValue.ACE) {
              this.outs = 9;
              this.nutOuts = 9;
            }else{
              this.outs = 13;
              this.nutOuts = 7;
            }
          }
        } else {
          // 16 out SD
          // has to be case with H's wrapping both top and bottom, with only one on one end while 
          // other end has 2 or 3 H's
          
          // HHHBBH (16,6)  HHBHBH (16,10) HBBHHH (16,16) HBHBHH (16,16)
          // AHHBBH (16,6)  AHBHBH (16,10) ABBHHH (12,12) ABHBHH (12,12)
          // HHHBBA (12,12) HHBHBA (12,12) HBBHHA (16,16) HBHBHA (16,16)

          if (this.lastFourHasThree()) {
            this.outs = 16;
            this.nutOuts = 16;

            if (this.holeCards[0].value === CardValue.ACE) {
              this.outs -= 4;
              this.nutOuts -= 4;
            }
          } else if (this.firstFourHasThree()) {
            this.outs = 16;
            this.nutOuts = 10;

            if (this.holeCards[this.holeCards.length - 1].value === CardValue.ACE) {
              this.outs = 12;
              this.nutOuts = 12;
            } else {
              if (this.runTemplate === "HHHBBH") {
                this.nutOuts = 6;
              } else {
                this.nutOuts = 10;
              }
            }
          } else {
            throw new Error("Problem in 16 out SD");
          }
        }
      }
    } else if (this.holeCards.length === 3) {
      if (this.board.length === 2) {
        // 3 hole cards, board 2 case
        if (this.runTemplate === "BHHHB") {
          // no A's to worry about
          // 9 out SD
          this.outs = 9;
          this.nutOuts = 9;
        } else if (this.runTemplate[0] === 'H' && 
                  this.runTemplate[this.runTemplate.length - 1] === 'H') {
          // 17 out SD
          // hole cards wrapping both above and below board cards

          // HBBHH (17,11) HBHBH (17,11) HHBBH (17, 7)
          //                             HHBBHA(17,11)
          // ABBHH (13,7)  ABHBH (13,7)  AHBBH (13,3)
          // HBBHA (13,13) HBHBA (13,13) HHBBA (13,13)

          if (this.holeCards[this.holeCards.length - 1].value === CardValue.ACE) {
            this.outs = 13;
            this.nutOuts = 13;
          } else if (this.holeCards[0].value === CardValue.ACE) {
            this.outs = 13;
            if (this.runTemplate === "HHBBH") {
              this.nutOuts = 3;
            } else {
              this.nutOuts = 7;
            }
          } else {
            this.outs = 17;
            if (this.runTemplate === "HHBBH") {
              // HHBBHA (17,11)
              if (this.holeCards[this.holeCards.length - 1].value === CardValue.KING) {
                this.nutOuts = 11;
              } else {
                // HHBBH (17,7)
                this.nutOuts = 7;
              }
            } else {
              this.nutOuts = 11;
            }
          }
        } else {
          // 13 out SD
          // cards wrapping either below or above the board cards but not both

          // BBHHH (13,13) BHBHH (13,13) BHHBH (13,13) HHHBB (13,3) HHBHB (13,3) HBHHB (13,7)
          //                                           HHHBBA(13,7) HHBHBA(13,7)
          // ABHHH ( 9,9)  AHBHH ( 9,9)  AHHBH ( 9,9)  AHHBB (13,3) AHBHB (13,3) ABHHB (13,7)
          // BBHHA (13,13) BHBHA (13,13) BHHBA (13,13) HHHBA ( 9,9) HHBHA ( 9,9) HBHHA ( 9,9)

          this.outs = 13;

          if (this.lastFourHasThree()) {
            this.nutOuts = 13;

            if (this.board[0].value === CardValue.ACE) {
              this.outs -= 4;
              this.nutOuts -= 4;
            }
          } else {
            if (this.board[this.board.length - 1].value === CardValue.ACE) {
              this.outs = 9;
              this.nutOuts = 9;
            } else if (this.runTemplate !== "HBHHB" && 
                      this.board[this.board.length - 1].value === CardValue.KING) {
              this.outs = 13;
              this.nutOuts = 7;
            } else {
              this.outs = 13;
              if (this.runTemplate === "HBHHB") {
                this.nutOuts = 7;
              } else {
                this.nutOuts = 3;
              }
            }
          }
        }
      }
    }
  }

  private getSafeCard(deck: Deck, startValue: number, runLength: number, isForHoleCards: boolean): Card {
    // Cards to avoid: the run sequence and cards within 2 positions of the boundaries
    const avoidValues = new Set<CardValue>();
    
    // Add all cards in the run sequence
    for (let i = 0; i < runLength; i++) {
      let value: CardValue;
      if (startValue + i < CardValue.TWO) {
        value = CardValue.ACE; // Wheel case
      } else {
        value = (startValue + i) as CardValue;
      }
      avoidValues.add(value);
    }
    
    // Add cards within ±2 positions from the sequence boundaries
    const firstValue = startValue < CardValue.TWO ? CardValue.ACE : startValue as CardValue;
    const lastValue = (startValue + runLength - 1) as CardValue;
    
    // Add cards before first value (up to 2 positions back)
    for (let offset = 1; offset <= 2; offset++) {
      if (firstValue === CardValue.TWO && offset === 1) {
        avoidValues.add(CardValue.ACE); // Before TWO in wheel
      } else if (firstValue === CardValue.THREE && offset === 2) {
        avoidValues.add(CardValue.ACE); // Two before THREE wraps to ACE
      } else if (firstValue !== CardValue.ACE && firstValue > CardValue.TWO) {
        const beforeValue = firstValue - offset;
        if (beforeValue >= CardValue.TWO) {
          avoidValues.add(beforeValue as CardValue);
        }
      }
    }
    
    // Add cards after last value (up to 2 positions forward)
    for (let offset = 1; offset <= 2; offset++) {
      if (lastValue !== CardValue.ACE && lastValue + offset <= CardValue.ACE) {
        avoidValues.add((lastValue + offset) as CardValue);
      }
    }

    // Count current suits to avoid creating flushes/flush draws
    const getCurrentSuitCounts = () => {
      const holeSuitCounts = {
        [Suit.HEARTS]: 0,
        [Suit.DIAMONDS]: 0,
        [Suit.CLUBS]: 0,
        [Suit.SPADES]: 0
      };

      const boardSuitCounts = {
        [Suit.HEARTS]: 0,
        [Suit.DIAMONDS]: 0,
        [Suit.CLUBS]: 0,
        [Suit.SPADES]: 0
      };

      // Count suits in hole cards
      this.holeCards.forEach(card => {
        holeSuitCounts[card.suit]++;
      });

      // Count suits in board cards
      this.board.forEach(card => {
        boardSuitCounts[card.suit]++;
      });

      return { holeSuitCounts, boardSuitCounts };
    };
    
    // Shuffle deck and deal cards until we get a safe one
    deck.shuffle();
    while (true) {
      const card = deck.dealCard();
      
      // Check if this card's value would create a straight
      if (avoidValues.has(card.value)) {
        continue; // Skip this card
      }

      // Check if this card's suit would create a flush or flush draw using Omaha rules
      const { holeSuitCounts, boardSuitCounts } = getCurrentSuitCounts();
      
      let newUsableCount;
      if (isForHoleCards) {
        const newHoleCount = holeSuitCounts[card.suit] + 1;
        // Must have at least 2 hole cards of a suit to potentially make a flush
        if (newHoleCount < 2) {
          // This card won't create a flush threat, card is safe
          return card;
        }
        const usableHoleCards = Math.min(newHoleCount, 2);
        const usableBoardCards = Math.min(boardSuitCounts[card.suit], 3);
        newUsableCount = usableHoleCards + usableBoardCards;
      } else {
        // Adding to board - still need at least 2 hole cards for this suit to matter
        if (holeSuitCounts[card.suit] < 2) {
          // This suit can't create a flush threat, card is safe
          return card;
        }
        const newBoardCount = boardSuitCounts[card.suit] + 1;
        const usableHoleCards = Math.min(holeSuitCounts[card.suit], 2);
        const usableBoardCards = Math.min(newBoardCount, 3);
        newUsableCount = usableHoleCards + usableBoardCards;
      }
      
      // Avoid creating flushes (5+ cards) or flush draws (exactly 4 cards)
      if (newUsableCount >= 4) {
        continue; // Skip this card
      }

      // Card is safe for both straights and flushes
      return card;
    }
  }

  private isFlush(): boolean {
    // Count cards of each suit from hole cards and board
    const holeSuitCounts = {
      [Suit.HEARTS]: 0,
      [Suit.DIAMONDS]: 0,
      [Suit.CLUBS]: 0,
      [Suit.SPADES]: 0
    };

    const boardSuitCounts = {
      [Suit.HEARTS]: 0,
      [Suit.DIAMONDS]: 0,
      [Suit.CLUBS]: 0,
      [Suit.SPADES]: 0
    };

    // Count suits in hole cards
    this.holeCards.forEach(card => {
      holeSuitCounts[card.suit]++;
    });

    // Count suits in board cards
    this.board.forEach(card => {
      boardSuitCounts[card.suit]++;
    });

    // Check if any suit has 5 or more cards using Omaha rules (max 2 from hole, max 3 from board)
    return Object.values(Suit).some(suit => {
      const usableHoleCards = Math.min(holeSuitCounts[suit], 2);
      const usableBoardCards = Math.min(boardSuitCounts[suit], 3);
      return usableHoleCards + usableBoardCards >= 5;
    });
  }

  private hasFlushDraw(): boolean {
    // Count cards of each suit from hole cards and board
    const holeSuitCounts = {
      [Suit.HEARTS]: 0,
      [Suit.DIAMONDS]: 0,
      [Suit.CLUBS]: 0,
      [Suit.SPADES]: 0
    };

    const boardSuitCounts = {
      [Suit.HEARTS]: 0,
      [Suit.DIAMONDS]: 0,
      [Suit.CLUBS]: 0,
      [Suit.SPADES]: 0
    };

    // Count suits in hole cards
    this.holeCards.forEach(card => {
      holeSuitCounts[card.suit]++;
    });

    // Count suits in board cards
    this.board.forEach(card => {
      boardSuitCounts[card.suit]++;
    });

    // Check if any suit has exactly 4 cards using Omaha rules
    // Must have at least 2 hole cards of a suit to potentially make a flush
    return Object.values(Suit).some(suit => {
      // Skip suits where we have fewer than 2 hole cards
      if (holeSuitCounts[suit] < 2) {
        return false;
      }
      
      const usableHoleCards = Math.min(holeSuitCounts[suit], 2);
      const usableBoardCards = Math.min(boardSuitCounts[suit], 3);
      return usableHoleCards + usableBoardCards === 4;
    });
  }

  /** The card value at each position of a run, given a starting value. */
  private static coreValues(template: string, startValue: number): CardValue[] {
    const values: CardValue[] = [];
    for (let i = 0; i < template.length; i++) {
      if (startValue + i < CardValue.TWO) {
        values.push(CardValue.ACE); // Wheel case: ace plays low
      } else {
        values.push((startValue + i) as CardValue);
      }
    }
    return values;
  }

  /** All distinct H/B arrangements for a given hole/board card count. */
  private static templatePermutations(holeCount: number, boardCount: number): string[] {
    const chars = 'H'.repeat(holeCount).split('').concat('B'.repeat(boardCount).split(''));
    const seen = new Set<string>();
    const recurse = (remaining: string[], current: string): void => {
      if (remaining.length === 0) {
        seen.add(current);
        return;
      }
      for (let i = 0; i < remaining.length; i++) {
        const next = remaining.slice();
        next.splice(i, 1);
        recurse(next, current + remaining[i]);
      }
    };
    recurse(chars, '');
    return Array.from(seen);
  }

  /**
   * Enumerates every valid (hole/board count, template, start value) and the
   * answer it produces, grouping configs by their "outs-nutOuts" answer. The
   * answer for each config is computed with the same calcOuts() the app uses,
   * so the mapping stays perfectly consistent.
   */
  private static buildCatalog(): AnswerCatalog {
    const byAnswer = new Map<string, HandConfig[]>();
    const probe = new WrapGenerator(true);

    const combos = [
      { holeCount: 3, boardCount: 2 },
      { holeCount: 4, boardCount: 2 },
      { holeCount: 4, boardCount: 3 },
    ];

    for (const { holeCount, boardCount } of combos) {
      for (const template of WrapGenerator.templatePermutations(holeCount, boardCount)) {
        const maxPossibleStart = CardValue.ACE - template.length + 1;
        for (let startValue = 1; startValue <= maxPossibleStart - 1; startValue++) {
          const values = WrapGenerator.coreValues(template, startValue);

          // Set up the probe with the core run cards (suit is irrelevant to
          // calcOuts, which only inspects card values).
          probe.runTemplate = template;
          probe.board = [];
          probe.holeCards = [];
          for (let i = 0; i < template.length; i++) {
            const card: Card = { suit: Suit.SPADES, value: values[i] };
            if (template[i] === 'B') {
              probe.board.push(card);
            } else {
              probe.holeCards.push(card);
            }
          }

          // Apply the same validity filters as the original generator.
          if (probe.isStraight() || template === 'BHHHHB') {
            continue;
          }

          probe.outs = 0;
          probe.nutOuts = 0;
          try {
            probe.calcOuts();
          } catch {
            continue; // Templates calcOuts can't resolve are not valid hands.
          }
          if (probe.outs <= 0) {
            continue; // Unhandled template (no real wrap) - skip.
          }

          const key = `${probe.outs}-${probe.nutOuts}`;
          const config: HandConfig = { holeCount, template, startValue };
          const existing = byAnswer.get(key);
          if (existing) {
            existing.push(config);
          } else {
            byAnswer.set(key, [config]);
          }
        }
      }
    }

    // Sampling weights are precomputed (see BALANCED_WEIGHTS) so we don't rerun
    // the optimizer every session. The outs rules never change, so the achievable
    // answer set - and therefore the optimal weights - are fixed.
    const keys = Array.from(byAnswer.keys());
    const rawWeights = keys.map(k => {
      const w = WrapGenerator.BALANCED_WEIGHTS[k];
      // Fall back to a uniform share if an answer ever appears that isn't in the
      // precomputed table (would only happen if calcOuts changes).
      return w ?? 1 / keys.length;
    });

    // Normalize so the weights sum to exactly 1 regardless of rounding.
    const sum = rawWeights.reduce((a, b) => a + b, 0);

    const cumulative: number[] = [];
    let acc = 0;
    for (const w of rawWeights) {
      acc += w / sum;
      cumulative.push(acc);
    }
    // Guard against floating-point drift so the final bucket always reaches 1.
    if (cumulative.length > 0) {
      cumulative[cumulative.length - 1] = 1;
    }

    return { byAnswer, keys, cumulative };
  }

  /**
   * Precomputed per-answer sampling weights ("outs-nutOuts" -> weight) that make
   * BOTH the total-outs and nut-outs marginals as close to uniform as possible
   * (a balanced compromise; with only a handful of valid pairs neither can be
   * made perfectly flat).
   *
   * These were produced once by minimizing the combined squared deviation of the
   * two marginals from uniform (gradient descent over a softmax parameterization).
   * Because the outs rules - and thus the achievable answers - are fixed, there is
   * no reason to recompute them at runtime. If calcOuts ever changes, regenerate
   * these values.
   */
  private static readonly BALANCED_WEIGHTS: Record<string, number> = {
    '13-3': 0.09258665801101497,
    '13-7': 0.0013096592002287966,
    '17-7': 0.09215001172100983,
    '17-11': 0.09345987972116754,
    '9-9': 0.13953505276691247,
    '13-13': 0.09258665801101496,
    '16-6': 0.06976732501160887,
    '16-10': 0.06976732501160887,
    '20-14': 0.13953505276691247,
    '12-12': 0.13953505276691247,
    '16-16': 0.06976732501160898,
  };

  private static getCatalog(): AnswerCatalog {
    if (!WrapGenerator.catalog) {
      WrapGenerator.catalog = WrapGenerator.buildCatalog();
    }
    return WrapGenerator.catalog;
  }

  private generate(): void {
    const catalog = WrapGenerator.getCatalog();

    // 1. Pick a random answer using balanced weights that keep both the
    //    total-outs and nut-outs marginals as close to uniform as feasible.
    const r = Math.random();
    let answerKey = catalog.keys[catalog.keys.length - 1];
    for (let i = 0; i < catalog.keys.length; i++) {
      if (r <= catalog.cumulative[i]) {
        answerKey = catalog.keys[i];
        break;
      }
    }
    const configs = catalog.byAnswer.get(answerKey)!;

    // 2. Pick a random hand that produces that answer. Retry on the rare
    //    chance the dealt suits create a flush/flush-draw, keeping the answer
    //    fixed so the answer distribution stays uniform.
    const MAX_ATTEMPTS = 2000;
    for (let attempt = 0; ; attempt++) {
      const config = configs[Math.floor(Math.random() * configs.length)];
      const deck = new Deck();

      this.board = [];
      this.holeCards = [];
      this.runTemplate = config.template;

      const values = WrapGenerator.coreValues(config.template, config.startValue);
      for (let i = 0; i < config.template.length; i++) {
        const card = deck.getAndRemoveRank(values[i]);
        if (config.template[i] === 'B') {
          this.board.push(card);
        } else {
          this.holeCards.push(card);
        }
      }

      this.calcOuts();

      // Fill board cards to 3 if needed - use safe cards
      while (this.board.length < 3) {
        this.board.push(this.getSafeCard(deck, config.startValue, config.template.length, false));
      }

      // Fill hole cards to 4 if needed - use safe cards
      while (this.holeCards.length < 4) {
        this.holeCards.push(this.getSafeCard(deck, config.startValue, config.template.length, true));
      }

      if (this.isFlush() || this.hasFlushDraw()) {
        if (attempt < MAX_ATTEMPTS) {
          continue; // Re-deal the same answer's cards.
        }
        return this.generate(); // Extremely unlikely; pick a fresh answer.
      }

      break;
    }

    // Shuffle the cards after ensuring valid hand
    this.board.sort(() => Math.random() - 0.5);
    this.holeCards.sort(() => Math.random() - 0.5);
  }

  public getBoard(): Card[] {
    return [...this.board];
  }

  public getHoleCards(): Card[] {
    return [...this.holeCards];
  }

  public getResult(): WrapResult {
    return {
      outs: this.outs,
      nutOuts: this.nutOuts
    };
  }
} 